import { useCallback, useEffect, useRef, useState } from 'react'
import { useServerLayoutNumber } from './useServerLayoutNumber.js'

const DEFAULT_LEFT_WIDTH = 300
const DEFAULT_RIGHT_WIDTH = 340
const MIN_LEFT_WIDTH = 240
const MIN_RIGHT_WIDTH = 300
const MAX_PANEL_WIDTH = 440
const MIN_CENTER_WIDTH = 520
const RESIZE_STEP = 24

const RESIZABLE_WORKSPACE_COLUMN_STORAGE_SCOPES = [
  'positionWorkspace',
  'positionWorkspaceChartLab',
]

export const RESIZABLE_WORKSPACE_COLUMN_STORAGE_KEYS = RESIZABLE_WORKSPACE_COLUMN_STORAGE_SCOPES.flatMap((scope) => [
  `erevis.${scope}.leftColumnWidth`,
  `erevis.${scope}.rightColumnWidth`,
])

function canUseStorage() {
  try {
    return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'
  } catch {
    return false
  }
}

export function preserveResizableWorkspaceColumnWidths(clearStorage) {
  if (typeof clearStorage !== 'function') return
  if (!canUseStorage()) {
    clearStorage()
    return
  }

  const preservedEntries = []
  for (const key of RESIZABLE_WORKSPACE_COLUMN_STORAGE_KEYS) {
    try {
      const value = window.localStorage.getItem(key)
      if (value !== null) preservedEntries.push([key, value])
    } catch {
      // Let the clear operation proceed even if a browser blocks reads.
    }
  }

  try {
    clearStorage()
  } catch {
    try {
      const keysToKeep = new Set(RESIZABLE_WORKSPACE_COLUMN_STORAGE_KEYS)
      const keysToRemove = []
      for (let index = 0; index < window.localStorage.length; index += 1) {
        const key = window.localStorage.key(index)
        if (key && !keysToKeep.has(key)) keysToRemove.push(key)
      }
      for (const key of keysToRemove) {
        try {
          window.localStorage.removeItem(key)
        } catch {
          // Clearing local preferences is best-effort when storage is blocked.
        }
      }
    } catch {
      // Width restoration below should still run if fallback cleanup fails.
    }
  }

  for (const [key, value] of preservedEntries) {
    try {
      window.localStorage.setItem(key, value)
    } catch {
      // The clear action already succeeded; persistence is best-effort here.
    }
  }
}

function viewportWidthFallback() {
  if (typeof window === 'undefined') return 1440
  return window.innerWidth || 1440
}

export function clampWorkspaceColumnWidth(width, side, viewportWidth = viewportWidthFallback()) {
  const minWidth = side === 'right' ? MIN_RIGHT_WIDTH : MIN_LEFT_WIDTH
  const rawResponsiveMax = Math.floor((Number(viewportWidth) - MIN_CENTER_WIDTH) / 2)
  const hasResponsiveBudget = Number.isFinite(rawResponsiveMax) && rawResponsiveMax > 0
  const clampedResponsiveMax = hasResponsiveBudget
    ? Math.min(MAX_PANEL_WIDTH, rawResponsiveMax)
    : minWidth
  const maxWidth = Math.max(minWidth, clampedResponsiveMax)
  return Math.min(maxWidth, Math.max(minWidth, Math.round(Number(width) || minWidth)))
}

export function useResizableWorkspaceColumns(storageScope = 'positionWorkspace', { userId, expandedRight = false } = {}) {
  const rowRef = useRef(null)
  const activeResizeStopRef = useRef(null)
  const left = useServerLayoutNumber({ userId, scope: storageScope, preferenceKey: 'left-column-width', defaultValue: DEFAULT_LEFT_WIDTH, min: MIN_LEFT_WIDTH, max: MAX_PANEL_WIDTH })
  const rightMax = expandedRight ? 1000 : MAX_PANEL_WIDTH
  const right = useServerLayoutNumber({ userId, scope: storageScope, preferenceKey: 'right-column-width', defaultValue: expandedRight ? 520 : DEFAULT_RIGHT_WIDTH, min: MIN_RIGHT_WIDTH, max: rightMax })
  const [availableWidth, setAvailableWidth] = useState(viewportWidthFallback)
  const leftWidth = clampWorkspaceColumnWidth(left.value, 'left', availableWidth)
  const clampRight = useCallback((value, width = availableWidth) => {
    if (!expandedRight) return clampWorkspaceColumnWidth(value, 'right', width)
    // Give the right rail the unused horizontal budget while keeping the left
    // rail and chart usable. Narrowing the viewport never overwrites saved size.
    const center = Math.min(MIN_CENTER_WIDTH, Math.max(0, width - leftWidth - MIN_RIGHT_WIDTH - 8))
    const max = Math.max(MIN_RIGHT_WIDTH, Math.min(rightMax, width - leftWidth - center - 8))
    return Math.max(MIN_RIGHT_WIDTH, Math.min(max, Math.round(Number(value) || MIN_RIGHT_WIDTH)))
  }, [availableWidth, expandedRight, leftWidth, rightMax])
  const rightWidth = clampRight(right.value)
  const { preview: setLeftWidth, commit: commitLeft } = left
  const { preview: setRightWidth, commit: commitRight } = right

  useEffect(() => {
    // A smaller viewport constrains display only, never the saved preference.
    const handleResize = () => setAvailableWidth(rowRef.current?.getBoundingClientRect?.().width || viewportWidthFallback())
    handleResize()
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(handleResize) : null
    if (rowRef.current) observer?.observe(rowRef.current)
    window.addEventListener('resize', handleResize)
    return () => { observer?.disconnect(); window.removeEventListener('resize', handleResize) }
  }, [])

  useEffect(() => {
    return () => {
      if (activeResizeStopRef.current) {
        activeResizeStopRef.current()
        activeResizeStopRef.current = null
      }
    }
  }, [userId, storageScope])

  const beginResize = useCallback((side, event) => {
    if ((event.button != null && event.button !== 0) || event.isPrimary === false) return
    const row = rowRef.current
    const rect = row?.getBoundingClientRect?.()
    if (!rect) return

    if (activeResizeStopRef.current) {
      activeResizeStopRef.current()
      activeResizeStopRef.current = null
    }

    event.preventDefault()
    const handle = event.currentTarget
    const pointerId = event.pointerId
    const originalCursor = document.body.style.cursor
    const originalUserSelect = document.body.style.userSelect
    handle?.setAttribute('data-resizing', 'true')
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'

    const applyClientX = (clientX) => {
      if (side === 'left') {
        setLeftWidth(clampWorkspaceColumnWidth(clientX - rect.left, 'left', rect.width))
      } else {
        setRightWidth(clampRight(rect.right - clientX, rect.width))
      }
    }

    const isActivePointer = (pointerEvent) => (
      pointerId == null || pointerEvent.pointerId == null || pointerEvent.pointerId === pointerId
    )
    const handleMove = (moveEvent) => {
      if (isActivePointer(moveEvent)) applyClientX(moveEvent.clientX)
    }
    let stopped = false
    const stopResize = () => {
      if (stopped) return
      stopped = true
      window.removeEventListener('pointermove', handleMove)
      window.removeEventListener('pointerup', handlePointerEnd)
      window.removeEventListener('pointercancel', handlePointerEnd)
      window.removeEventListener('blur', stopResize)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      handle?.removeEventListener?.('lostpointercapture', stopResize)
      if (pointerId != null && handle?.hasPointerCapture?.(pointerId)) {
        try {
          handle.releasePointerCapture(pointerId)
        } catch {
          // The browser may already have released capture during pointerup.
        }
      }
      handle?.removeAttribute('data-resizing')
      document.body.style.cursor = originalCursor
      document.body.style.userSelect = originalUserSelect
      activeResizeStopRef.current = null
      if (side === 'left') commitLeft(); else commitRight()
    }
    const handlePointerEnd = (pointerEvent) => {
      if (isActivePointer(pointerEvent)) stopResize()
    }
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') stopResize()
    }

    applyClientX(event.clientX)
    window.addEventListener('pointermove', handleMove)
    window.addEventListener('pointerup', handlePointerEnd)
    window.addEventListener('pointercancel', handlePointerEnd)
    window.addEventListener('blur', stopResize)
    document.addEventListener('visibilitychange', handleVisibilityChange)
    handle?.addEventListener?.('lostpointercapture', stopResize)
    activeResizeStopRef.current = stopResize
    if (pointerId != null) {
      try {
        handle?.setPointerCapture?.(pointerId)
      } catch {
        // Window-level listeners and blur cleanup remain as the fallback.
      }
    }
  }, [setLeftWidth, setRightWidth, commitLeft, commitRight, clampRight])

  const handleKeyboardResize = useCallback((side, event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
    event.preventDefault()
    const viewportWidth = rowRef.current?.getBoundingClientRect?.().width || viewportWidthFallback()
    const direction = event.key === 'ArrowRight' ? 1 : -1
    if (side === 'left') {
      commitLeft(clampWorkspaceColumnWidth(leftWidth + direction * RESIZE_STEP, 'left', viewportWidth))
    } else {
      commitRight(clampRight(rightWidth - direction * RESIZE_STEP, viewportWidth))
    }
  }, [leftWidth, rightWidth, commitLeft, commitRight, clampRight])

  const getResizeHandleProps = useCallback((side) => {
    const viewportWidth = rowRef.current?.getBoundingClientRect?.().width || viewportWidthFallback()
    return {
      role: 'separator',
      tabIndex: 0,
      'aria-orientation': 'vertical',
      'aria-label': side === 'left' ? 'Resize positions column' : 'Resize workspace tools column',
      'aria-valuemin': side === 'right' ? MIN_RIGHT_WIDTH : MIN_LEFT_WIDTH,
      'aria-valuemax': side === 'right' ? clampRight(rightMax, viewportWidth) : clampWorkspaceColumnWidth(MAX_PANEL_WIDTH, side, viewportWidth),
      'aria-valuenow': side === 'left' ? leftWidth : rightWidth,
      onPointerDown: (event) => beginResize(side, event),
      onKeyDown: (event) => handleKeyboardResize(side, event),
      className: 'group flex w-2 shrink-0 touch-none cursor-col-resize items-stretch justify-center self-stretch rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ink/40',
    }
  }, [beginResize, handleKeyboardResize, leftWidth, rightWidth, clampRight, rightMax])

  return {
    rowRef,
    layoutPreferences: [left, right],
    leftWidth,
    rightWidth,
    getResizeHandleProps,
    resizeHandleMarkerClassName: 'my-1 w-px rounded-full bg-transparent transition-colors group-hover:bg-inkMuted/70 group-focus-visible:bg-inkMuted group-data-[resizing=true]:bg-inkMuted',
  }
}
